import { useReveal } from '../../../hooks/useReveal';

const steps = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Initial Strategy Call',
    description:
      'We start with a strategy call to understand your market, target case types, and growth goals. Every campaign is built specifically for your firm.',
  },
  {
    number: '02',
    icon: 'ri-google-line',
    title: 'Targeted Google Ads Campaign Launch',
    description:
      'We build and lunch hyper-targeted Google Ads campaigns designed to capture high-intent personal injury searches in your local market.',
  },
  {
    number: '03',
    icon: 'ri-user-received-line',
    title: 'Exclusive Leads Delivered',
    description:
      'Leads are delivered directly to your intake team and only your firm. No sharing, no competition, no reselling.',
  },
  {
    number: '04',
    icon: 'ri-bar-chart-2-line',
    title: 'Scale Your Case Volume',
    description:
      'We monitor performance daily, cut waste, and double down on what works. As your pipeline grows, we scale your campaign to match your capacity.',
  },
];

export default function HowItWorksSection() {
  const { ref: headRef, isVisible: headVisible } = useReveal<HTMLDivElement>(0.2);
  const { ref: stepsRef, isVisible: stepsVisible } = useReveal<HTMLDivElement>(0.1);

  return (
    <section
      id="how-it-works"
      className="py-32 px-6 lg:px-12 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #0e0e0e 100%)' }}
    >
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[1px] pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.15), transparent)' }}
      />

      <div className="max-w-7xl mx-auto">
        <div ref={headRef} className="text-center mb-14 md:mb-20">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
            style={{
              fontFamily: 'Playfair Display, serif',
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s',
            }}
          >
            How We Generate <span className="text-[#D4AF37]">Personal Injury Cases</span>
          </h2>
          <p
            className="text-white/50 text-base max-w-2xl mx-auto mt-5 leading-relaxed"
            style={{
              fontFamily: 'Inter, sans-serif',
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.7s ease 0.25s, transform 0.7s ease 0.25s',
            }}
          >
            Built specifically for personal injury law firms looking to generate consistent case opportunities.
          </p>
        </div>

        <div
          ref={stepsRef}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
        >
          {steps.map((step, index) => (
            <div
              key={index}
              className="group flex gap-4 md:gap-6 border border-white/[0.06] rounded-lg p-6 md:p-8 hover:border-[#D4AF37]/25 transition-all duration-500"
              style={{
                background: '#111111',
                opacity: stepsVisible ? 1 : 0,
                transform: stepsVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s, border-color 0.5s ease`,
              }}
            >
              <div className="flex-shrink-0 pt-1">
                <span
                  className="block text-5xl font-bold leading-none select-none"
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    color: 'rgba(212,175,55,0.12)',
                  }}
                >
                  {step.number}
                </span>
              </div>
              <div className="flex-1">
                <div className="w-10 h-10 flex items-center justify-center border border-[#D4AF37]/20 rounded-md mb-4 group-hover:border-[#D4AF37]/50 transition-colors duration-300">
                  <i className={`${step.icon} text-lg text-[#D4AF37]`} />
                </div>
                <h3
                  className="text-xl font-bold text-white mb-3"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-white/50 text-sm leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-14 flex justify-center"
          style={{
            opacity: stepsVisible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.5s',
          }}
        >
          <button
            onClick={() => {
              const el = document.getElementById('consultation');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="flex items-center gap-3 text-[#D4AF37] text-sm font-medium hover:gap-5 transition-all duration-300 cursor-pointer"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Book Your Strategy Call Today
            <div className="w-8 h-px bg-[#D4AF37]" />
            <i className="ri-arrow-right-line text-base" />
          </button>
        </div>
      </div>
    </section>
  );
}